import { useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getDecisionFormRef,
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { DECISION_VIOLATIONS } from 'CorePackage/MainModule/routes/routes';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { convertViolationDataToKnmErknmFormViolatedRequirementsNpa } from 'DecisionPackage/DecisionWillBeAct/functions/convertViolationDataToKnmErknmFormViolatedRequirementsNpa';
import { getViolationsByReasonLink } from 'DecisionPackage/DecisionWillBeAct/functions/getViolationsByReasonLink';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { DecisionInitialViolations } from './DecisionInitialViolations';
import { DecisionRepeatViolation } from './DecisionRepeatViolation/DecisionRepeatViolation';

interface IDecisionViolationsProps {
  watchOnly: boolean;
  isViolationFieldsWatchOnly: boolean;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  isRequirements: IBooleanSelectOption | undefined;
  objects?: IKnmErknmFormObject[];
  reasons: IDecisionFormReason[] | undefined;
  created: string | undefined;
  inBody?: boolean;
}

interface IDecisionViolationsStoreProps {
  formRef: IDecisionFormRef | undefined;
  isSupervisor: boolean;
  isDecisionStatusFillInProcess: boolean;
  settings: IAdminSetting[];
  isPassportByCheckPrescriptionFailed: boolean | undefined;
  isPassportByCheckPrescriptionOldRules: boolean | undefined;
}

const DecisionViolationsWithoutStore = ({
  watchOnly,
  isViolationFieldsWatchOnly,
  formRef,
  knoOrganization,
  supervisionType,
  objects,
  isRequirements,
  isDecisionStatusFillInProcess,
  isSupervisor,
  created,
  reasons,
  settings,
  inBody,
  isPassportByCheckPrescriptionFailed,
  isPassportByCheckPrescriptionOldRules,
}: IDecisionViolationsProps & IDecisionViolationsStoreProps) => {
  const isDisableViolations = !isSupervisor || !isDecisionStatusFillInProcess;

  const requirementsFieldName = 'violatedMandatoryRequirements';
  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [initValues, setInitValues] = useState<IRequirementsNpaInitValues | undefined>(undefined);
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );
  const reason = reasons?.[0];

  const isPrescriptionCheck =
    reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK && isPassportByCheckPrescriptionOldRules === false;

  const openRequirementsModal = () => {
    setInitValues(undefined);
    setIsRequirementsModalVisible(true);
  };

  const handleIsViolatedMandatoryRequirementsChange = useCallback(
    async (v: IBooleanSelectOption) => {
      if (v.value) {
        const requirements = await getViolationsByReasonLink(
          isEnablePrescriptionCheck,
          isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true,
          reason?.links?.[0],
        );
        const violation = convertViolationDataToKnmErknmFormViolatedRequirementsNpa(requirements);

        formRef?.current.change(requirementsFieldName as keyof IDecisionForm, violation);
      } else {
        formRef?.current.change(requirementsFieldName as keyof IDecisionForm);
      }
    },
    [formRef, isEnablePrescriptionCheck, isPrescriptionCheck, reason],
  );

  return (
    <div id={DECISION_VIOLATIONS}>
      <FieldBlock label="Выявленные нарушения обязательных требований" size="half">
        <Row>
          <SelectInputField
            className="shared-row-flex"
            name={'isViolatedMandatoryRequirements'}
            options={booleanSelectOptions}
            isClearable={false}
            disabled={watchOnly || isDisableViolations}
            onChange={handleIsViolatedMandatoryRequirementsChange}
            size="medium"
          />
          {!isPrescriptionCheck && isRequirements?.value === true && (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              id="decisionPassportNpaAddButton"
              disabled={watchOnly || !knoOrganization || !supervisionType || isDisableViolations}
              onClick={openRequirementsModal}
            />
          )}
        </Row>
      </FieldBlock>
      {isPrescriptionCheck ? (
        <DecisionRepeatViolation checkboxDisabled={!isDecisionStatusFillInProcess} formRef={formRef} inBody={inBody} />
      ) : (
        <DecisionInitialViolations
          isViolationFieldsWatchOnly={isViolationFieldsWatchOnly}
          knoOrganization={knoOrganization}
          supervisionType={supervisionType}
          watchOnly={watchOnly}
          objects={objects}
          isRequirementsModalVisible={isRequirementsModalVisible}
          initValues={initValues}
          setIsRequirementsModalVisible={val => setIsRequirementsModalVisible(val)}
          setInitValues={(val: IRequirementsNpaInitValues | undefined) => setInitValues(val)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionViolationsStoreProps => ({
  formRef: getDecisionFormRef(state),
  settings: getAdminFetchSettings(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  isPassportByCheckPrescriptionFailed: getDecisionIsPrescriptionFailed(state),
  isPassportByCheckPrescriptionOldRules: getDecisionIsPrescriptionOldRules(state),
});

export const DecisionViolations = connect(mapStateToProps)(DecisionViolationsWithoutStore);
